import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Seo from "@/components/Seo";

export default function Register() {
  const { register, error } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const preRole = sp.get("role") === "business" ? "business" : "consumer";
  const refCode = (sp.get("ref") || "").toUpperCase().slice(0, 6);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", zip_code: "",
    date_of_birth: "", password: "",
    consent_rules: false, consent_privacy: false, consent_marketing: false,
    role: preRole,
    referral_code: refCode,
  });
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_rules || !form.consent_privacy) {
      toast.error("Please accept the Official Rules and Privacy Policy.");
      return;
    }
    setBusy(true);
    try {
      const user = await register(form);
      toast.success("Welcome to ADN Local FanFest!");
      nav(user.role === "business" ? "/business" : "/dashboard");
    } catch (err) {
      toast.error(err.message || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      <Seo
        title="Register Free — Enter to Win"
        description="Register free for ADN Local FanFest 2026. 60-second entry. Must be 18+. One entry per person/household. No purchase necessary. Grand Prize: 6 days / 5 nights in Cancun for two, airfare included + $500 cash."
      />
      <div className="text-xs tracking-[0.3em] uppercase text-primary">Free Entry</div>
      <h1 className="font-bebas text-5xl mt-2">REGISTER TO WIN</h1>
      <p className="text-white/60 mt-2 text-sm">No purchase necessary. One entry per family/household. Must be 18+.</p>

      {refCode && (
        <div className="mt-4 flex items-center gap-3 border border-primary/40 bg-primary/10 rounded p-3" data-testid="referral-banner">
          <div className="text-2xl">🎁</div>
          <div className="text-sm">
            You&apos;re registering with referral code <b className="text-primary">{refCode}</b>. Your friend gets a bonus sweepstakes entry when you complete registration.
          </div>
        </div>
      )}

      <form onSubmit={submit} className="mt-8 adn-card p-6 sm:p-8 space-y-5" data-testid="register-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="First name" v={form.first_name} onChange={set("first_name")} required data-testid="register-first-name" autoComplete="given-name" name="first_name" />
          <Field label="Last name" v={form.last_name} onChange={set("last_name")} required data-testid="register-last-name" autoComplete="family-name" name="last_name" />
        </div>
        <Field label="Email" type="email" v={form.email} onChange={set("email")} required data-testid="register-email" autoComplete="email" inputMode="email" name="email" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Phone" type="tel" v={form.phone} onChange={set("phone")} required data-testid="register-phone" autoComplete="tel" inputMode="tel" name="phone" />
          <Field label="ZIP code" v={form.zip_code} onChange={set("zip_code")} required data-testid="register-zip" autoComplete="postal-code" inputMode="numeric" name="postal_code" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Date of birth" type="date" v={form.date_of_birth} onChange={set("date_of_birth")} required data-testid="register-dob" autoComplete="bday" name="bday" />
          <Field label="Password (min 6)" type="password" v={form.password} onChange={set("password")} required data-testid="register-password" autoComplete="new-password" name="new-password" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/60">I am registering as</label>
          <div className="mt-2 flex gap-2">
            {["consumer","business"].map(r=>(
              <button type="button" key={r} onClick={()=>setForm(f=>({...f, role:r}))}
                className={`pill-btn ${form.role===r?"active":""}`}
                data-testid={`register-role-${r}`}>
                {r === "consumer" ? "Fan / Consumer" : "Business / Sponsor"}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2 pt-2">
          <Check label="I confirm I am 18+ and only ONE entry per family/household." v={form.consent_rules} onChange={set("consent_rules")} testid="register-consent-rules" />
          <Check label="I agree to the Official Rules & Privacy Policy." v={form.consent_privacy} onChange={set("consent_privacy")} testid="register-consent-privacy" />
          <Check label="(Optional) Send me deals and event updates via email/SMS." v={form.consent_marketing} onChange={set("consent_marketing")} testid="register-consent-marketing" />
        </div>

        {error && <div className="text-destructive text-sm" data-testid="register-error">{error}</div>}

        <button disabled={busy}
          data-testid="register-submit"
          className="w-full px-6 py-4 bg-primary text-black font-bebas text-2xl tracking-wider rounded-sm hover:brightness-110 disabled:opacity-60 min-h-[56px]">
          {busy ? "SUBMITTING…" : "ENTER NOW"}
        </button>
        <p className="text-[11px] text-white/50">
          By registering you enter the ADN Local FanFest sweepstakes. See{" "}
          <Link to="/rules" className="underline">Official Rules</Link> and{" "}
          <Link to="/privacy" className="underline">Privacy Policy</Link>.
        </p>
        <p className="text-xs text-white/70 pt-2">
          Already registered? <Link to="/login" className="text-primary underline">Sign in</Link>
        </p>
      </form>
    </div>
  );
}

function Field({ label, v, onChange, type = "text", required, ...rest }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-white/60">{label}</span>
      <input {...rest}
        value={v} onChange={onChange} type={type} required={required}
        className="mt-1 block w-full bg-[#0b1428] border border-adn-border rounded px-3 py-3 focus:border-primary outline-none text-sm" />
    </label>
  );
}
function Check({ label, v, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 text-sm text-white/80">
      <input type="checkbox" checked={v} onChange={onChange} data-testid={testid}
        className="mt-1 accent-primary w-4 h-4" />
      <span>{label}</span>
    </label>
  );
}
